import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { Observable, Subscription, filter } from "rxjs";
import { AppState } from "src/app/store/app.reducer";
import { CustomValidators } from "src/app/validators/custom.validators";
import { StandardNewValidator, StandardPageLayout } from "src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib";
import * as DeviceVctActions from 'src/app/store/device-vct/device-vct.actions';
import { selectDeviceVctShared, selectDeviceVctSharing } from "src/app/store/device-vct/device-vct.selector";
import { CreateInvitationVct } from "src/app/model/device-vct/device-vct-invitations";
import { selectUser } from "src/app/store/user/user.selector";
import { User } from "src/app/model/user";

@Component({
	templateUrl: './share-device-vct.dialog.component.html',
})

export class ShareDeviceVctDialogComponent implements OnInit, OnDestroy {

	currentUser: User;
	currentUser$: Observable<User | undefined>;
	currentUserSub: Subscription | undefined;

	shareDeviceForm: FormGroup;

	deviceName: string;

	productId: number;
	moduleId: number;
	featureId: number;

	deviceVctShared$: Observable<boolean | undefined>;
	deviceVctSharedSub: Subscription;
	deviceVctSharing: boolean;
	deviceVctSharing$: Observable<boolean | undefined>;
	deviceVctSharingSub: Subscription;

	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private dialogRef: DynamicDialogRef,
		public config: DynamicDialogConfig
	) {
		if (config.data !== undefined && this.config.data.productId !== undefined) {
			this.productId = this.config.data.productId;
		} else {
			this.productId = undefined;
		}
		if (config.data !== undefined && this.config.data.moduleId !== undefined) {
			this.moduleId = this.config.data.moduleId;
		} else {
			this.moduleId = undefined;
		}
		if (config.data !== undefined && this.config.data.featureId !== undefined) {
			this.featureId = this.config.data.featureId;
		} else {
			this.featureId = undefined;
		}
		this.initSelectors();
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.buildForm();
		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	initSelectors(): void {
		this.deviceVctShared$ = this.store.select(selectDeviceVctShared);
		this.deviceVctSharing$ = this.store.select(selectDeviceVctSharing);
		this.currentUser$ = this.store.select(selectUser);
	}

	subscribeAll(): void {
		this.deviceVctSharedSub = this.deviceVctShared$.pipe(filter(res => !!res))
			.subscribe(() => {
				this.dialogRef.close('success');
			}
			);
		this.deviceVctSharingSub = this.deviceVctSharing$?.subscribe(
			(sharing) => {
				this.deviceVctSharing = sharing;
			}
		);
		this.currentUserSub = this.currentUser$?.subscribe(
			(currentUser) => {
				this.currentUser = currentUser;
			}
		);
	}

	ngOnDestroy(): void {
		this.deviceVctSharedSub?.unsubscribe();
		this.deviceVctSharingSub?.unsubscribe();
		this.currentUserSub?.unsubscribe();
	}

	buildForm(): void {
		this.shareDeviceForm = new FormGroup({
			'email': new FormControl('', [Validators.required, Validators.email]),
			'emailConfirm': new FormControl('', [Validators.required, Validators.email]),
			'cleanup': new FormControl(false),
		});
		this.shareDeviceForm.setValidators(CustomValidators.checkMatchingFields('email', 'emailConfirm'));
	}

	get f(): any {
		return this.shareDeviceForm.controls;
	}

	closeDialog(): void {
		this.dialogRef.close();
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.shareDeviceForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.shareDeviceForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	assignToMe(): void {
		this.f.email.setValue(this.currentUser.Email);
		this.f.emailConfirm.setValue(this.currentUser.Email);
	}

	save(): void {
		const payload = new CreateInvitationVct(this.f.email.value);
		let accept = false;
		if (this.productId !== undefined) {
			payload.SiteproductId = this.productId;
		}
		if (this.moduleId !== undefined) {
			payload.SitemoduleId = this.moduleId;
		}
		if (this.featureId !== undefined) {
			payload.SitefeatureId = this.featureId;
		}
		if (this.currentUser.Email === payload.Email) {
			accept = true;
		}
		// invitation: CreateInvitationVct, cleanup: boolean
		this.store.dispatch(DeviceVctActions.createVctInvitations({ invitation: payload, cleanup: this.f.cleanup.value, accept: accept }));
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	col12Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-20rem');
	}

	justifyMessagesNewValidator(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: ShareDeviceVctDialogComponent["f"], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorsConfirmFields(type: ShareDeviceVctDialogComponent["f"], classType: string, notEquivalentType: string): string {
		return StandardNewValidator.showErrorsConfirmFields(type, classType, notEquivalentType, this.screenWidth, this.shareDeviceForm);
	}

	showErrorRequiredMessage(type: ShareDeviceVctDialogComponent["f"]): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	showErrorConfirmInvalidMessage(type: ShareDeviceVctDialogComponent["f"], notEquivalentType: string): boolean {
		return StandardNewValidator.showErrorConfirmInvalidMessage(type, notEquivalentType, this.shareDeviceForm);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}
}